'use client';

import { useTranslations } from 'next-intl';
import { HelpCircle } from 'lucide-react';
import { Input } from '@/shared/ui/shadcn/input';
import { Label } from '@/shared/ui/shadcn/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/shadcn/popover';
import type { ProjectAttributes } from '@/types/project';

interface FacadeAreaFieldProps {
  id: string;
  /** Local string buffer (same plain-buffer pattern as goals/region). */
  value: string;
  unit: NonNullable<ProjectAttributes['facade_area_unit']>;
  /** Show the "estimated from photos" caption under the field. */
  isEstimated: boolean;
  disabled?: boolean;
  onChange: (raw: string) => void;
  onBlur: () => void;
}

/** Keep only digits and a single decimal separator (comma normalized to dot). */
export function sanitizeFacadeAreaInput(raw: string): string {
  const normalized = raw.replace(/,/g, '.').replace(/[^0-9.]/g, '');
  const [head = '', ...rest] = normalized.split('.');
  return rest.length > 0 ? `${head}.${rest.join('')}` : head;
}

/**
 * Buffer string → PATCH value. Empty clears the area (null); anything
 * non-numeric is "not a valid edit" (undefined → field omitted).
 */
export function parseFacadeArea(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * Optional facade-area input for the project goals surfaces (standalone page,
 * render-settings sheet, project settings panel). The value is entered in the
 * project's display unit (server-resolved m²/ft²); estimates use it as the
 * authoritative area. When the server back-filled the value from an LLM
 * estimate, a caption says so until the user takes ownership by editing.
 */
export function FacadeAreaField({
  id,
  value,
  unit,
  isEstimated,
  disabled = false,
  onChange,
  onBlur,
}: FacadeAreaFieldProps) {
  const t = useTranslations('project');
  const unitLabel = unit === 'ft2' ? t('facadeAreaUnits.ft2') : t('facadeAreaUnits.m2');

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label htmlFor={id}>{t('facadeArea')}</Label>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="start">
            <p className="text-form-help text-muted-foreground">
              {t('facadeAreaHelpText')}
            </p>
          </PopoverContent>
        </Popover>
      </div>
      <div className="relative">
        <Input
          id={id}
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(sanitizeFacadeAreaInput(e.target.value))}
          onBlur={onBlur}
          placeholder={t('facadeAreaPlaceholder')}
          maxLength={10}
          disabled={disabled}
          className="pe-12"
        />
        <span className="pointer-events-none absolute inset-y-0 end-3 flex items-center text-form-help text-muted-foreground">
          {unitLabel}
        </span>
      </div>
      {isEstimated && (
        <p className="text-form-help text-muted-foreground">
          {t('facadeAreaEstimatedCaption')}
        </p>
      )}
    </div>
  );
}
